import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Label shown in the fallback UI. */
  label?: string;
  /** Optional callback to open the feedback form pre-filled with the error. */
  onReportError?: (errorMessage: string) => void;
}
interface State {
  hasError: boolean;
  message?: string;
  /** Bumped on each recovery attempt to force child remount. */
  retryCount: number;
}

/**
 * React error boundary wrapping each screen AND each panel (PSD §5.3 / TODO CC.6).
 * No telemetry — PHI protection. Logs to console only.
 *
 * Recovery uses a key-bump so children are fully remounted (not just re-rendered),
 * clearing any corrupted internal state.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, retryCount: 0 };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message, retryCount: 0 };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[AMG ErrorBoundary]', this.props.label ?? '', error, info.componentStack);
  }

  handleReload = (): void => {
    this.setState((prev) => ({ hasError: false, message: undefined, retryCount: prev.retryCount + 1 }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="panel panel-error" role="alert">
          <h3 className="panel-title">Something went wrong</h3>
          <p className="panel-error-body">
            {this.props.label ? `${this.props.label}: ` : ''}
            {this.state.message ?? 'An unexpected error occurred.'}
          </p>
          <p className="panel-error-body">
            Try again to reload this section. Your data is safe.
          </p>
          <button className="btn" onClick={this.handleReload}>
            Try again
          </button>
          {this.props.onReportError && (
            <button
              className="btn btn-secondary"
              onClick={() => this.props.onReportError?.(this.state.message ?? 'Unknown error')}
            >
              Report this error
            </button>
          )}
        </div>
      );
    }
    return <div key={`eb-${this.state.retryCount}`}>{this.props.children}</div>;
  }
}
